import ProjectLinks from 'src/app/common/project.links';
import { EntityStatus, ItemStatus } from 'src/app/interfaces/views.interface';
import { AccountStatus, ConnectionStatus, FullUser, UserBasic, UserRole } from 'src/app/swagger-api';

export interface ButtonState {
  label: string;
  disabled: boolean;
  hidden: boolean;
}

export const defaultBtnSate: ButtonState = {
  disabled: true,
  hidden: true,
  label: '',
};

export type ConnectionActionType = 'Link' | 'Unlink' | 'Accept' | 'Pending';

export const connectionActionType: (regularUser: FullUser, sessionUser: FullUser) => ConnectionActionType = (
  regularUser: FullUser,
  sessionUser: FullUser,
) => {
  const { reaction } = regularUser;
  const status: ConnectionStatus =
    (reaction.connection_details && reaction.connection_details.status) || ConnectionStatus.None;
  const isInitiator = reaction.connection_details?.initiator_id === sessionUser.user_id;

  const type: Record<ConnectionStatus, ConnectionActionType> = {
    Verified: 'Unlink',
    Pending: isInitiator ? 'Pending' : 'Accept',
    None: 'Link',
  };

  return type[status];
};

export const doctorLinkActionType: (regularUser: FullUser, sessionUser: FullUser) => ConnectionActionType = (
  regularUser: FullUser,
  sessionUser: FullUser,
) => {
  const { reaction } = regularUser;
  const status: ConnectionStatus =
    (reaction.doctor_link_status && reaction.doctor_link_status.status) || ConnectionStatus.None;
  const isInitiator = reaction.doctor_link_status?.initiator_id === sessionUser.user_id;

  const type: Record<ConnectionStatus, ConnectionActionType> = {
    Verified: 'Unlink',
    Pending: isInitiator ? 'Pending' : 'Accept',
    None: 'Link',
  };

  return type[status];
};

export const patientLinkActionType: (regularUser: FullUser, sessionUser: FullUser) => ConnectionActionType = (
  regularUser: FullUser,
  sessionUser: FullUser,
) => {
  const { reaction } = regularUser;
  const status: ConnectionStatus =
    (reaction.patient_link_status && reaction.patient_link_status.status) || ConnectionStatus.None;
  const isInitiator = reaction.patient_link_status?.initiator_id === sessionUser.user_id;

  const type: Record<ConnectionStatus, ConnectionActionType> = {
    Verified: 'Unlink',
    Pending: isInitiator ? 'Pending' : 'Accept',
    None: 'Link',
  };

  return type[status];
};

export const connectionButtonState: (regularUser: FullUser, sessionUser: FullUser) => ButtonState = (
  regularUser: FullUser,
  sessionUser: FullUser,
) => {
  if (sessionUser.is_admin || regularUser.is_admin) {
    return defaultBtnSate;
  }

  const actionType = connectionActionType(regularUser, sessionUser);
  const label: Record<ConnectionActionType, string> = {
    Unlink: 'PROFILE_VIEW_REMOVE_FROM_MY_CONNECTIONS_BTN_HINT',
    Link: 'PROFILE_VIEW_ADD_TO_MY_CONNECTIONS_BTN_HINT',
    Accept: 'PROFILE_VIEW_ACCEPT_CONNECTION_BTN_HINT',
    Pending: 'PROFILE_VIEW_REQUEST_SENT_CONNECTION_BTN_HINT',
  };

  return {
    disabled: actionType == 'Pending',
    label: label[actionType],
    hidden: false,
  };
};

export const doctorLinkButtonState: (regularUser: FullUser, sessionUser: FullUser) => ButtonState = (
  regularUser: FullUser,
  sessionUser: FullUser,
) => {
  if (
    !regularUser.user_roles.includes(UserRole.Doctor) ||
    !sessionUser.user_roles.includes(UserRole.Patient) ||
    sessionUser.is_admin
  ) {
    return defaultBtnSate;
  }

  const actionType = doctorLinkActionType(regularUser, sessionUser);

  const label: Record<ConnectionActionType, string> = {
    Unlink: 'UNLINK_DOCTOR_BTN',
    Link: 'LINK_DOCTOR_BTN',
    Accept: 'ACCEPT_DOCTOR_REQUEST',
    Pending: 'REQUEST_IS_SENT_BUTTON',
  };

  return {
    disabled: actionType == 'Pending',
    label: label[actionType],
    hidden: false,
  };
};

export const patientLinkButtonState: (regularUser: FullUser, sessionUser: FullUser) => ButtonState = (
  regularUser: FullUser,
  sessionUser: FullUser,
) => {
  if (
    !regularUser.user_roles.includes(UserRole.Patient) ||
    !sessionUser.user_roles.includes(UserRole.Doctor) ||
    sessionUser.is_admin
  ) {
    return defaultBtnSate;
  }

  const actionType = patientLinkActionType(regularUser, sessionUser);

  const label: Record<ConnectionActionType, string> = {
    Unlink: 'UNLINK_PATIENT_BTN',
    Link: 'LINK_PATIENT_BTN',
    Accept: 'ACCEPT_PATIENT_REQUEST',
    Pending: 'REQUEST_IS_SENT_BUTTON',
  };

  return {
    disabled: actionType == 'Pending',
    label: label[actionType],
    hidden: false,
  };
};

export function getStatus(user: UserBasic): EntityStatus {
  if (user.account_status === AccountStatus.DEACTIVATED) {
    return ItemStatus.DEACTIVATED;
  } else if (user.reaction?.blocked) {
    return ItemStatus.BLOCKED;
  } else return user.account_status;
}

export function createLink(status: EntityStatus, userId: string, accessForAdmin = false): string {
  if (
    status === AccountStatus.ACTIVE ||
    status === ItemStatus.BLOCKED ||
    (status === AccountStatus.DEACTIVATED && accessForAdmin)
  ) {
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${userId}`;
  } else {
    return '';
  }
}
